// src/libs/aiSearch.js
// Central AI helpers + extractors + Firestore save/fetch utilities.
// Adjust import path for your firebase export if needed.

import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
    collection,
    addDoc,
    serverTimestamp,
    getDoc,
    doc as fbDoc,
    runTransaction
} from "firebase/firestore";

import { db } from "../firebase"; // <-- ensure this path points to your firebase export
// runtime-config aware API base (reads window._env injected into public/index.html)
const API_BASE = (typeof window !== 'undefined' && window._env && window._env.API_BASE)
    || process.env.REACT_APP_API_BASE
    || 'https://api.jift.io'; // fallback to same origin if empty

// ---------------- small helpers ----------------
function pick(obj, ...paths) {
    if (!obj) return null;
    for (const p of paths) {
        const parts = p.split(".");
        let cur = obj;
        let ok = true;
        for (const rawPart of parts) {
            if (cur == null) {
                ok = false;
                break;
            }
            const part = rawPart;
            // support 'images[0]' and numeric '0' tokens
            const arrIndex = part.match(/^(.+)\[(\d+)\]$/);
            if (arrIndex) {
                const key = arrIndex[1];
                const idx = Number(arrIndex[2]);
                cur = cur[key];
                cur = Array.isArray(cur) ? cur[idx] : undefined;
            } else if (/^\d+$/.test(part)) {
                cur = Array.isArray(cur) ? cur[Number(part)] : undefined;
            } else {
                cur = cur[part];
            }
        }
        if (!ok) continue;
        if (typeof cur === "string" && cur.trim()) return cur;
        if (Array.isArray(cur) && cur.length) return cur;
        if (typeof cur === "number") return cur;
        if (typeof cur === "object" && cur && Object.keys(cur).length) return cur;
    }
    return null;
}
function slugify(text = "") {
    return text
        .toLowerCase()
        .replace(/['"‘’“”]/g, "")
        .replace(/[^a-z0-9\s-]/g, " ")
        .trim()
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
}


// ---------------- auth helper ----------------
export function waitForAuthUser(timeoutMs = 2000) {
    return new Promise((resolve) => {
        const auth = getAuth();
        if (auth.currentUser) return resolve(auth.currentUser);
        let done = false;
        const unsub = onAuthStateChanged(auth, (u) => {
            if (done) return;
            done = true;
            try { unsub(); } catch (e) { }
            resolve(u);
        });
        setTimeout(() => {
            if (done) return;
            done = true;
            try { unsub(); } catch (e) { }
            resolve(null);
        }, timeoutMs);
    });
}

// ---------------- overview + normalize ----------------
export function extractOverview(aiCandidate) {
    if (!aiCandidate) return null;

    const rawOverview =
        aiCandidate.overview ??
        aiCandidate.destination_overview ??
        aiCandidate.destinationInfo ??
        null;

    if (rawOverview) {
        if (typeof rawOverview === "string") {
            return { destination_info: { description: rawOverview } };
        }
        if (typeof rawOverview === "object") return rawOverview;
    }

    const destInfo =
        aiCandidate?.overview?.destination_info ??
        aiCandidate?.destination_info ??
        aiCandidate?.details?.destination ??
        null;

    if (destInfo && typeof destInfo === "object") {
        return { destination_info: destInfo };
    }

    const description =
        aiCandidate?.description ??
        aiCandidate?.summary ??
        aiCandidate?.about ??
        (aiCandidate?.content && (aiCandidate.content.description || aiCandidate.content.summary)) ??
        null;

    if (description && typeof description === "string") {
        return { destination_info: { description } };
    }

    return null;
}

export function normalizeAiPayload(aiRaw) {
    const ai = aiRaw || {};

    const title = pick(ai, "title", "name", "tripTitle") || (typeof ai === "string" ? ai.split("\n")[0] : null);
    const summary = pick(ai, "summary", "description", "overview", "content.summary") || (typeof ai === "string" ? ai : "");
    let tags = pick(ai, "tags");
    if (!tags) {
        const tagsStr = pick(ai, "tagString", "topics", "meta.tags");
        if (typeof tagsStr === "string") tags = tagsStr.split(/[,#]/).map(s => s.trim()).filter(Boolean);
    }
    if (!tags) tags = ["ai", "suggested"];

    const trip_days = pick(ai, "trip_days", "days", "duration") || (Array.isArray(ai.itinerary) ? ai.itinerary.length : null);

    const overview = extractOverview(ai);

    const normalized = {
        id: "ai-" + Date.now(),
        title: title || "AI Trip Plan",
        summary: summary || "",
        excerpt: summary ? String(summary).split("\n")[0] : null,
        tags: Array.isArray(tags) ? tags : (typeof tags === "string" ? tags.split(/[,#]/).map(s => s.trim()).filter(Boolean) : ["ai", "suggested"]),
        source: "ai",
        bgImage: pick(ai, "bgImage", "image", "hero", "images[0]") || null,
        trip_days,
        budget: pick(ai, "budget", "price", "estimate") || null,
        overview,
        highlights: pick(ai, "highlights", "top") || null,
        logistics: pick(ai, "logistics", "notes") || null,
        packing: pick(ai, "packing") || null,
        map: pick(ai, "map") || null,
        itinerary: pick(ai, "itinerary", "days", "plan", "route") || null,
        raw: ai,
        createdAt: new Date().toISOString(),
    };

    return normalized;
}

// ---------------- Firestore save ----------------
export async function saveAiTripToFirestore(aiTripObj) {
    try {
        const user = await waitForAuthUser();
        const createdByUid = user && user.uid ? user.uid : "guest";

        // --- Normalize Tags ---
        let rawTags = [];

        if (Array.isArray(aiTripObj.tags)) {
            rawTags = aiTripObj.tags;
        } else if (aiTripObj.tags) {
            rawTags = [aiTripObj.tags];
        } else {
            rawTags = ["suggested"];
        }

        const tagSet = new Set(
            rawTags
                .map(t => (typeof t === "string" ? t.trim().toLowerCase() : String(t).trim().toLowerCase()))
                .filter(Boolean)
        );

        tagSet.add("ai");
        const finalTags = Array.from(tagSet);

        const base = {
            title: aiTripObj.title || aiTripObj.summary?.split?.("\n")?.[0] || "AI Trip Plan",
            summary: aiTripObj.summary || aiTripObj.excerpt || null,
            excerpt: aiTripObj.excerpt || (aiTripObj.summary && String(aiTripObj.summary).split("\n")[0]) || null,
            tags: finalTags,
            source: "ai",
            bgImage: aiTripObj.bgImage || aiTripObj.image || null,
            trip_days: typeof aiTripObj.trip_days !== "undefined" ? aiTripObj.trip_days : null,
            itinerary: aiTripObj.itinerary || null,
            overview: aiTripObj.overview || null,
            raw: aiTripObj.raw || aiTripObj || null,
            visibility: aiTripObj.visibility || "private",
            savedFromSearch: true,
            createdByUid,
            createdByGuest: createdByUid === "guest",
            createdAt: serverTimestamp(),
        };

        if (createdByUid !== "guest") {
            base.budget = aiTripObj.budget || null;
            base.highlights = aiTripObj.highlights || null;
            base.logistics = aiTripObj.logistics || null;
            base.packing = aiTripObj.packing || null;
            base.map = aiTripObj.map || null;
        }

        // -------------------------------
        // 1️⃣ Create AI Trip First
        // -------------------------------
        const aiColl = collection(db, "ai_trips");
        const docRef = await addDoc(aiColl, base);
        const tripId = docRef.id;

        // -------------------------------
        // 2️⃣ Generate Unique Slug
        // -------------------------------
        const baseSlug = slugify(base.title || "ai-trip");
        const shortId = tripId.slice(0, 6);
        const finalSlug = `${baseSlug}-${shortId}`;

        const slugRef = fbDoc(db, "slugs", finalSlug);
        const aiTripRef = fbDoc(db, "ai_trips", tripId);

        // -------------------------------
        // 3️⃣ Transaction = No Duplicates
        // -------------------------------
        await runTransaction(db, async (transaction) => {
            const slugSnap = await transaction.get(slugRef);

            if (slugSnap.exists()) {
                throw new Error("Slug collision detected (extremely rare).");
            }

            transaction.set(slugRef, {
                tripId,
                ownerId: createdByUid,
                type: "ai_trip",
                createdAt: serverTimestamp(),
            });

            transaction.update(aiTripRef, {
                slug: finalSlug,
            });
        });

        return {
            id: tripId,
            slug: finalSlug,
            path: `ai_trips/${tripId}`,
            createdByUid
        };

    } catch (err) {
        console.error("aiSearch.saveAiTripToFirestore failed:", err?.code, err?.message || err);
        return null;
    }
}

// ---------------- fetch AI results ----------------
export async function fetchAiResults(queryText) {
    if (!queryText) return [];
    try {
        // construct url using runtime API base (strip trailing slashes)
        const url = `${API_BASE.replace(/\/+$/, '')}/api/ai/combined-search`;
        // if API_BASE is empty string, url becomes "/api/ai/combined-search" (same-origin)
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ query: queryText }),
        });

        const data = await res.json();
        const ai = data?.aiItinerary ?? data?.ai ?? data?.itinerary ?? data?.plan ?? data;

        const summary = ai?.summary ?? ai?.description ?? ai?.overview ?? "";
        const title = ai?.title ?? ai?.name ?? (summary ? String(summary).split(".")[0] : "AI Trip Plan");
        const itinerary = ai?.itinerary ?? ai?.plan ?? ai?.days ?? null;

        let tags = [];
        if (Array.isArray(ai?.tags)) tags = ai.tags;
        else if (typeof ai?.tags === "string") tags = ai.tags.split(/[,#]/).map(s => s.trim()).filter(Boolean);

        if (!tags.length) {
            const ht = (title + " " + summary).match(/#([a-zA-Z0-9-_]+)/g);
            if (ht) tags = ht.map(t => t.replace(/^#/, ""));
        }

        const overview = extractOverview(ai);

        const normalized = {
            id: "ai-" + Date.now(),
            title,
            excerpt: summary,
            tags: tags.length ? tags : ["ai", "suggested"],
            source: "ai",
            raw: ai,
            itinerary,
            overview,
            highlights: ai?.highlights ?? null,
            logistics: ai?.logistics ?? ai?.notes ?? null,
            packing: ai?.packing ?? null,
            map: ai?.map ?? null,
            bgVideo: ai?.bgVideo || ai?.video || null,
        };

        return [normalized];
    } catch (err) {
        console.error("aiSearch.fetchAiResults failed:", err);
        return [];
    }
}

// ---------------- fine-grained extractors (all inside this file) ----------------

// Basic scalar extractors
export function extractTitle(ai) {
    if (!ai) return "";
    const title = pick(ai, "title", "name", "tripTitle", "metadata.title") ||
        (typeof ai === "string" ? ai.split("\n")[0] : null);
    return title || "";
}

export function extractSummary(ai) {
    if (!ai) return "";
    const summary = pick(ai, "summary", "description", "overview", "content.summary") ||
        (typeof ai === "string" ? ai : null);
    return summary || "";
}

export function extractTags(ai) {
    if (!ai) return ["ai", "suggested"];
    let tags = pick(ai, "tags");
    if (Array.isArray(tags)) return tags;
    if (typeof tags === "string") return tags.split(/[,#]/).map(s => s.trim()).filter(Boolean);
    const tagStr = pick(ai, "tagString", "topics", "meta.tags");
    if (typeof tagStr === "string") return tagStr.split(/[,#]/).map(s => s.trim()).filter(Boolean);
    // fallback: try to detect inline #hashtags in title/summary
    const text = (extractTitle(ai) + " " + extractSummary(ai)).trim();
    const ht = text.match(/#([a-zA-Z0-9-_]+)/g);
    if (ht) return ht.map(t => t.replace(/^#/, ""));
    return ["ai", "suggested"];
}

export function extractBgImage(ai) {
    if (!ai) return "";
    return pick(ai, "bgImage", "image", "hero", "images.0", "media.hero") || "";
}

export function extractTripDays(ai) {
    if (!ai) return 0;
    const days = pick(ai, "trip_days", "days", "duration", "metadata.days");
    if (typeof days === "number") return days;
    if (typeof days === "string" && /^\d+$/.test(days.trim())) return Number(days.trim());
    if (Array.isArray(ai?.itinerary)) return ai.itinerary.length;
    return 0;
}

export function extractSource(ai) {
    return pick(ai, "source", "origin", "meta.source") || "ai";
}

// Budget object
export function extractBudget(ai) {
    const raw = pick(ai, "budget", "price", "estimate", "cost");
    if (!raw) {
        return {
            total: "",
            currency: "",
            approx_value: 0,
            notes: ""
        };
    }
    // raw can be string or object
    if (typeof raw === "string") {
        const m = raw.match(/([A-Za-z₹$€£]+)?\s*([\d,\.]+)/);
        return {
            total: raw,
            currency: (m && m[1]) ? m[1] : "",
            approx_value: m ? Number(String(m[2]).replace(/,/g, "")) : 0,
            notes: ""
        };
    }
    // object shape
    return {
        total: raw.total ?? raw.amount ?? "",
        currency: raw.currency ?? raw.curr ?? "",
        approx_value: (typeof raw.approx_value === "number") ? raw.approx_value : (raw.amount ? Number(String(raw.amount).replace(/,/g, "")) : 0),
        notes: raw.notes ?? raw.note ?? ""
    };
}

// Overview -> destination_info
export function extractDestinationInfo(ai) {
    const dest = pick(ai, "overview.destination_info", "destination_info", "destination", "place");
    const fallbackDesc = pick(ai, "description", "summary", "about");
    const out = {
        description: (dest && dest.description) || fallbackDesc || "",
        location: (dest && (dest.location || dest.city || dest.region)) || pick(ai, "location", "meta.location") || "",
        best_time_to_visit: (dest && (dest.best_time_to_visit || dest.best_time)) || pick(ai, "best_time_to_visit", "meta.best_time") || "",
        languages: (dest && (Array.isArray(dest.languages) ? dest.languages : (typeof dest.languages === "string" ? dest.languages.split(/[,;]/).map(s => s.trim()).filter(Boolean) : null))) || (pick(ai, "languages") || []),
        local_transport: (dest && (dest.local_transport || dest.transport)) || pick(ai, "local_transport", "transport") || ""
    };
    return out;
}

// Overview -> historical_facts
export function extractHistoricalFacts(ai) {
    const hist = pick(ai, "overview.historical_facts", "historical_facts", "history", "background");
    return {
        colonial_past: (hist && (hist.colonial_past || hist.colonialHistory || hist.colonial)) || pick(ai, "colonial_past", "history.colonial") || "",
        architecture: (hist && (hist.architecture || hist.architectural_style)) || pick(ai, "architecture", "history.architecture") || "",
        cultural_blend: (hist && (hist.cultural_blend || hist.culture || hist.cultural)) || pick(ai, "culture", "history.cultural_blend") || ""
    };
}

// Overview -> traveller_insights
export function extractTravellerInsights(ai) {
    const t = pick(ai, "overview.traveller_insights", "traveller_insights", "traveler_insights", "tips") || {};
    return {
        local_experience: t.local_experience || t.localExperience || pick(ai, "local_experience", "tips.local") || "",
        food_tip: t.food_tip || t.foodTip || pick(ai, "food_tip", "tips.food") || "",
        safety_tip: t.safety_tip || t.safetyTip || pick(ai, "safety_tip", "tips.safety") || "",
        hidden_gems: t.hidden_gems || t.hiddenGems || pick(ai, "hidden_gems", "tips.hidden_gems") || []
    };
}

// highlights, logistics, packing, map
export function extractHighlights(ai) {
    const h = pick(ai, "highlights", "top", "topHighlights", "overview.highlights");
    if (!h) return [];
    if (Array.isArray(h)) return h;
    if (typeof h === "string") return h.split(/[\n,;|]+/).map(s => s.trim()).filter(Boolean);
    return [];
}

export function extractLogistics(ai) {
    return pick(ai, "logistics", "notes", "practical_info", "overview.logistics") || "";
}

export function extractPacking(ai) {
    const p = pick(ai, "packing", "packingList", "pack", "checklist");
    if (!p) return [];
    if (Array.isArray(p)) return p;
    if (typeof p === "string") return p.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    if (typeof p === "object") return Object.values(p).flat().filter(Boolean);
    return [];
}

export function extractMap(ai) {
    return pick(ai, "map", "maps", "overview.map", "location.mapUrl") || "";
}

// itinerary + nested activities
export function extractItinerary(ai) {
    const rawItin = pick(ai, "itinerary", "plan", "days", "route", "schedule") || null;
    if (!rawItin) return [];
    if (Array.isArray(rawItin)) {
        return rawItin.map(normalizeItineraryDay).filter(Boolean);
    }
    if (typeof rawItin === "object") {
        const keys = Object.keys(rawItin);
        if (rawItin.title || rawItin.activities) return [normalizeItineraryDay(rawItin)];
        return keys.map(k => normalizeItineraryDay(rawItin[k], k)).filter(Boolean);
    }
    if (typeof rawItin === "string") {
        const days = rawItin.split(/\n(?=Day\s*\d+[:\-]?)|(?=\bDay\s*\d+\b)/i);
        return days.map(d => ({ title: "", summary: d.trim(), activities: [] })).filter(Boolean);
    }
    return [];
}

function normalizeItineraryDay(dayObj = {}, key = null) {
    if (!dayObj) return null;
    if (typeof dayObj === "string") {
        return { title: key || "", summary: dayObj, activities: [] };
    }
    const title = dayObj.title || dayObj.name || (key ? String(key) : "");
    const summary = dayObj.summary || dayObj.note || dayObj.description || "";
    const activitiesRaw = dayObj.activities || dayObj.items || dayObj.plans || [];
    const activities = Array.isArray(activitiesRaw) ? activitiesRaw.map(normalizeActivity).filter(Boolean) : [];
    return { title, summary, activities };
}

function normalizeActivity(act = {}) {
    if (!act) return null;
    if (typeof act === "string") {
        return { time: "", title: act, location: "", description: "", recommended_places: [], tips: "" };
    }
    return {
        time: act.time || act.at || "",
        title: act.title || act.name || act.activity || "",
        location: act.location || act.place || act.venue || "",
        description: act.description || act.desc || act.note || "",
        recommended_places: act.recommended_places || act.recommendedPlaces || act.places || [],
        tips: act.tips || act.tip || ""
    };
}

